import { useState, useEffect, useMemo } from 'preact/hooks'

const TIER_FOOTWEAR = 'FOOTWEAR'
const TIER_APPAREL = 'APPAREL'

const TIER_LABELS = {
  [TIER_FOOTWEAR]: 'Footwear',
  [TIER_APPAREL]: 'Apparel & Accessories'
}

const FOOTWEAR_GENDERS = ['MENS', 'WOMENS', 'KIDS']

const DEFAULT_GENDER = 'MENS'

const GENDER_PREFIX_MAP = {
  W: 'WOMENS',
  M: 'MENS',
  K: 'KIDS'
}

const GENDER_LABELS = {
  WOMENS: 'Womens',
  MENS: 'Mens',
  KIDS: 'Kids'
}

/**
 * Mens leads unless the collection sets custom.size_filter_gender (Mens/Womens/Kids),
 * exposed on window.collection by the collection-main section.
 */
const getPreferredGender = () => {
  const override = String(window.collection?.size_filter_gender ?? '').trim().toUpperCase()
  return FOOTWEAR_GENDERS.includes(override) ? override : DEFAULT_GENDER
}

export const UniversalSizeFilter = ({ values, onFilter }) => {
  const { footwear, apparel } = useMemo(() => parseSizeData(values), [values])

  const genderOrder = useMemo(() => {
    const preferredGender = getPreferredGender()
    return [preferredGender, ...FOOTWEAR_GENDERS.filter((gender) => gender !== preferredGender)]
  }, [])

  const footwearGenders = useMemo(
    () => genderOrder.filter((gender) => footwear[gender]?.length > 0),
    [genderOrder, footwear]
  )

  const availableTiers = useMemo(() => {
    const tiers = []
    if (footwearGenders.length > 0) tiers.push(TIER_FOOTWEAR)
    if (apparel.length > 0) tiers.push(TIER_APPAREL)
    return tiers
  }, [footwearGenders, apparel])

  const [activeTier, setActiveTier] = useState('')
  const [activeGender, setActiveGender] = useState('')

  useEffect(() => {
    if (availableTiers.length > 0 && !availableTiers.includes(activeTier)) {
      setActiveTier(availableTiers[0])
    }
  }, [availableTiers, activeTier])

  useEffect(() => {
    if (footwearGenders.length > 0 && !footwearGenders.includes(activeGender)) {
      setActiveGender(footwearGenders[0])
    }
  }, [footwearGenders, activeGender])

  const gridSizes =
    activeTier === TIER_FOOTWEAR ? footwear[activeGender] || [] : apparel

  return (
    <div className="flex flex-col">
      {availableTiers.length > 1 && (
        <div className="flex items-center gap-x-1 overflow-x-scroll no-scrollbar overscroll-x-none">
          {availableTiers.map((tier) => (
            <button
              key={tier}
              type="button"
              onClick={() => setActiveTier(tier)}
              className="btn btn-text btn-primary btn-m w-fit whitespace-nowrap"
            >
              <span className={`${activeTier === tier ? 'text-line text-line-large' : ''}`}>{TIER_LABELS[tier]}</span>
            </button>
          ))}
        </div>
      )}

      {activeTier === TIER_FOOTWEAR && footwearGenders.length > 1 && (
        <div className="flex items-center w-full bg-page-tertiary p-0.5 mb-1 overflow-x-scroll no-scrollbar overscroll-x-none">
          {footwearGenders.map((gender) => (
            <div
              key={gender}
              className={`btn btn-s w-[90px] md:w-[75px] xl:w-[90px] whitespace-nowrap ${
                activeGender === gender ? 'btn-fill btn-secondary' : 'btn-transparent btn-primary'
              }`}
              onClick={() => setActiveGender(gender)}
            >
              <span>{GENDER_LABELS[gender]}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-0.5 w-full">
        {gridSizes.map((item) => (
          <SizeGridItem key={item.value} item={item} onFilter={onFilter} />
        ))}
      </div>
    </div>
  )
}

const SizeGridItem = ({ item, onFilter }) => {
  return (
    <div
      className={`
        btn btn-primary btn-border btn-m w-full max-w-[calc((100%-(3*0.5rem))/4)] !px-0.125
        ${item.checked ? 'active' : ''}
      `}
      onClick={() => onFilter(item.value)}
    >
      <input
        type="checkbox"
        checked={item.checked}
        onChange={() => onFilter(item.value)}
        className="sr-only"
        aria-label={item.displayLabel || item.label}
      />
      <span>{item.size || item.label}</span>
    </div>
  )
}

const CLOTHING_SIZE_ORDER = ['5XS', '4XS', '3XS', '2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL']

const clothingSizeIndex = (size) => {
  const upperSize = size.trim().toUpperCase()
  const repeatedX = upperSize.match(/^(X{2,})([SL])$/)
  const normalised = repeatedX ? `${repeatedX[1].length}X${repeatedX[2]}` : upperSize

  return CLOTHING_SIZE_ORDER.indexOf(normalised)
}

const numericSize = (size) => (/^\d+(\.\d+)?$/.test(size.trim()) ? parseFloat(size) : NaN)

/**
 * Parse ss_combined_sizes filter values (formatted as PREFIX/SIZE) into two tiers:
 * - footwear: bucketed by gender (W/M/K prefixes)
 * - apparel: A/ prefixed sizes plus anything that isn't a footwear gender
 */
const parseSizeData = (values) => {
  const footwear = { WOMENS: [], MENS: [], KIDS: [] }
  const apparel = []

  values.forEach((item) => {
    const label = typeof item.label === 'string' ? item.label : String(item.label ?? '')
    const slashIndex = label.indexOf('/')

    if (slashIndex > 0) {
      const prefix = label.slice(0, slashIndex).trim().toUpperCase()
      const size = label.slice(slashIndex + 1).trim()
      const gender = GENDER_PREFIX_MAP[prefix]

      if (gender) {
        footwear[gender].push({ ...item, size, displayLabel: `${GENDER_LABELS[gender]} ${size}` })
        return
      }

      apparel.push({ ...item, size, displayLabel: size })
      return
    }

    apparel.push({ ...item, size: label, displayLabel: label })
  })

  FOOTWEAR_GENDERS.forEach((gender) => {
    footwear[gender].sort(compareSizes)
  })
  apparel.sort(compareSizes)

  return { footwear, apparel }
}

// Numeric sizes first, then clothing sizes in CLOTHING_SIZE_ORDER, then anything unrecognised
const compareSizes = (a, b) => {
  const sizeA = String(a.size ?? '')
  const sizeB = String(b.size ?? '')

  const idxA = clothingSizeIndex(sizeA)
  const idxB = clothingSizeIndex(sizeB)
  if (idxA !== -1 && idxB !== -1) return idxA - idxB

  const numA = numericSize(sizeA)
  const numB = numericSize(sizeB)
  const aIsNum = !isNaN(numA)
  const bIsNum = !isNaN(numB)

  if (aIsNum && bIsNum) return numA - numB
  if (aIsNum) return -1
  if (bIsNum) return 1

  if (idxA !== -1) return -1
  if (idxB !== -1) return 1

  return sizeA.localeCompare(sizeB, undefined, { numeric: true })
}
